import React, { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { LINKS } from "@/lib/links";

const YT_ID = "oE82j5Og6Vc";

const TAKEAWAYS = [
  {
    numeral: "I.",
    slug: "sit-still",
    title: "Your money should never sit still.",
    body:
      "Most savings systems put your money to work for the bank, not for you. The goal is a structure where your dollars keep compounding even while you're using them.",
    expanded:
      "Your savings account pays you 0.5% while the bank lends your money out at 7, 8, 12 percent. You are the product. Every dollar sitting in a traditional account is a dollar working harder for the institution holding it than for the person who earned it. The shift isn’t about finding a better savings rate. It’s about changing whose system your money is building.",
  },
  {
    numeral: "II.",
    slug: "banking-function",
    title: "The banking function is the opportunity.",
    body:
      "Banks profit by controlling the flow of money — not by risking their own. When you understand this, you start asking a different question: what if I controlled that function instead?",
    expanded:
      "Banks don’t take risks with their own money. They take risks with yours — and keep the spread. They borrow cheap, lend expensive, and pocket the difference. For centuries that function has been one of the most profitable in human history. And it’s been entirely outsourced by the people actually generating the wealth. Understanding this doesn’t make you cynical. It makes you strategic. Because once you see the mechanism, you can’t unsee it — and you start building accordingly.",
  },
  {
    numeral: "III.",
    slug: "vehicle",
    title: "Whole life insurance is the vehicle, not the destination.",
    body:
      "A dividend-paying whole life policy from a mutual company is the tool that makes this possible. It's not about the death benefit — it's about liquidity, control, and uninterrupted growth.",
    expanded:
      "Most people hear ‘whole life insurance’ and think they’re being sold something. That reaction is understandable — the industry has earned it. But the people who dismissed it are still parking money in accounts they can’t touch, paying taxes on growth they didn’t choose, and asking banks for access to capital they already own. The policy is a container. What matters is what it does — cash that grows guaranteed, that you can access without penalty, that doesn’t stop compounding when you use it. No other financial tool does all three simultaneously. That’s not a sales pitch. That’s just the math.",
  },
  {
    numeral: "IV.",
    slug: "process",
    title: "This is a process, not a product.",
    body:
      "Infinite Banking isn't something you buy. It's something you build — a private system that gets more powerful the longer you use it.",
    expanded:
      "There’s no version of this where you write a check and wake up financially free. What there is — is a system that rewards consistency and time in a way almost nothing else does. Every premium paid, every loan cycled through, every dollar that stays in motion inside your own structure compounds the whole thing. Five years in it’s useful. Ten years in it’s powerful. Twenty years in it becomes the most important financial decision you ever made. The people who benefit most aren’t the ones who understood it perfectly on day one. They’re the ones who started.",
  },
];

export default function HowItWorksPage() {
  const [openSlug, setOpenSlug] = useState(null);

  return (
    <div className="min-h-screen bg-paper text-nearblack font-sans antialiased">
      <Nav heroVisible={false} />

      {/* Section 1 — Intro */}
      <section className="pt-32 md:pt-44 pb-16 md:pb-20" data-testid="hiw-intro">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <p className="eyebrow" data-testid="hiw-intro-eyebrow">
            How It Works
          </p>
          <h1
            data-testid="hiw-intro-headline"
            className="font-serif text-nearblack mt-7 leading-[1.05] text-balance"
            style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 300 }}
          >
            The system most people were{" "}
            <em className="italic text-camel" style={{ fontWeight: 300 }}>
              never shown
            </em>
            .
          </h1>
          <span className="block w-[60px] h-px bg-camel mt-10" />
          <p className="mt-10 text-nearblack font-light text-[18px] leading-[1.8] max-w-[640px]">
            Most financial advice focuses on what to do with your money — save more, invest more,
            wait longer. This is about something different. It&apos;s about how money actually
            moves, and how to build a structure that keeps you in control of it.
          </p>
          <p
            className="mt-6 font-serif italic text-camel"
            style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.55rem)", fontWeight: 300 }}
          >
            Watch the video below to see the full breakdown.
          </p>
        </div>
      </section>

      {/* Section 2 — Video embed */}
      <section className="pb-20 md:pb-28" data-testid="hiw-video">
        <div className="max-w-[1080px] mx-auto px-6 md:px-10">
          <div
            className="relative w-full bg-nearblack overflow-hidden"
            style={{ paddingTop: "56.25%" }}
          >
            <iframe
              data-testid="hiw-video-iframe"
              src={`https://www.youtube.com/embed/${YT_ID}?rel=0&modestbranding=1`}
              title="Liz DiRocco — How It Works"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
            />
          </div>
        </div>
      </section>

      {/* Section 3 — Key Takeaways */}
      <section className="py-20 md:py-28 border-t border-paperdeep" data-testid="hiw-takeaways">
        <div className="max-w-[960px] mx-auto px-6 md:px-10">
          <p className="eyebrow">What You Just Learned</p>
          <h2
            className="font-serif text-nearblack mt-7 leading-[1.1] text-balance"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.6rem)", fontWeight: 400 }}
          >
            The core ideas, <em className="italic text-camel">distilled</em>.
          </h2>
          <p className="mt-6 text-camel/85 text-[11px] tracking-[0.22em] uppercase font-medium">
            Click each idea to read more
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-paperdeep">
            {TAKEAWAYS.map((t, i) => {
              const isOpen = openSlug === t.slug;
              return (
                <button
                  key={t.slug}
                  type="button"
                  onClick={() => setOpenSlug(isOpen ? null : t.slug)}
                  aria-expanded={isOpen}
                  aria-controls={`hiw-panel-${t.slug}`}
                  data-testid={`hiw-takeaway-${i + 1}`}
                  className={`text-left bg-paper p-8 md:p-10 group transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-camel ${
                    isOpen ? "bg-paperdeep/60" : "hover:bg-paperdeep/60"
                  }`}
                >
                  <div className="flex items-baseline justify-between mb-8">
                    <span
                      className="font-serif italic text-camel"
                      style={{ fontSize: "clamp(1.8rem, 2.8vw, 2.1rem)", fontWeight: 300 }}
                    >
                      {t.numeral}
                    </span>
                    <span className="block h-px bg-camel/40 flex-1 mx-6" />
                    <Plus
                      className={`shrink-0 w-4 h-4 text-camel transition-transform duration-300 ${
                        isOpen ? "rotate-45" : "rotate-0"
                      }`}
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                  </div>
                  <h3
                    className="font-serif text-nearblack leading-[1.2]"
                    style={{ fontSize: "clamp(1.3rem, 2.1vw, 1.55rem)", fontWeight: 400 }}
                    data-testid={`hiw-takeaway-trigger-${t.slug}`}
                  >
                    {t.title}
                  </h3>
                  <p className="mt-4 text-nearblack/80 font-light text-[16px] leading-[1.8]">
                    {t.body}
                  </p>

                  {/* Expandable panel */}
                  <div
                    id={`hiw-panel-${t.slug}`}
                    data-testid={`hiw-panel-${t.slug}`}
                    className={`grid transition-all duration-500 ease-out ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100 mt-6"
                        : "grid-rows-[0fr] opacity-0 mt-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="pt-5 border-t border-camel/40">
                        <p className="text-nearblack/85 leading-[1.85] text-[15.5px] font-light">
                          {t.expanded}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4 — CTA (calm) */}
      <section
        className="bg-nearblack text-paper py-20 md:py-28"
        data-testid="hiw-cta"
      >
        <div className="max-w-[680px] mx-auto px-6 md:px-10 text-center">
          <p className="eyebrow-light">Ready to See How This Applies to You</p>
          <h2
            className="font-serif text-paper mt-7 leading-[1.05]"
            style={{ fontSize: "clamp(2rem, 4.5vw, 2.85rem)", fontWeight: 300 }}
          >
            Let&apos;s look at your situation{" "}
            <em className="italic text-camellight" style={{ fontWeight: 300 }}>
              specifically
            </em>
            .
          </h2>
          <p className="mt-8 text-paper/85 text-[17.5px] leading-[1.8] font-light max-w-[560px] mx-auto">
            Every financial structure is different. A Money Clarity Call is a chance to ask your
            questions, see real numbers, and find out whether this approach makes sense for where
            you are right now.
          </p>
          <a
            href={LINKS.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hiw-cta-button"
            className="mt-12 btn-camel inline-flex items-center justify-center gap-2 group"
          >
            Book a Money Clarity Call
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <p className="mt-5 text-paper/50 italic text-[13px]">
            30 minutes · Free · No pitch
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
